import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { PageHeader, Card, CardTitle, GoldButton, GhostButton, Input, Select, colors, fonts } from '../components/UI'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'
import { getPayCycle } from '../lib/payday'
import { THEMES, useTheme } from '../lib/theme'

export default function SettingsPage({ onNavigate }) {
  const { user, profile, fetchProfile, signOut } = useAuth()
  const { themeId, setTheme } = useTheme()
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg, setPwMsg] = useState('')
  const [showDelete, setShowDelete] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteErr, setDeleteErr] = useState('')
  const [deleted, setDeleted] = useState(false)

  const [form, setForm] = useState({
    full_name: '',
    income_type: 'single',
    income_1: '',
    income_2: '',
    income_1_label: 'My Income',
    income_2_label: 'Partner Income',
    currency: 'GBP',
    savings_rate_target: 20,
    emergency_fund_target_months: 6,
    pay_day: '',
    pay_day_shift_weekends: true,
  })

  // Generic field setter - pages have used this pattern from day one but the
  // helper itself was never defined here, so every input silently no-op'd on
  // change. Fixing it.
  const setF = (key, value) => setForm(f => ({ ...f, [key]: value }))

  const [hintsEnabled, setHintsEnabled] = useState(true)
  const [includeCCinSTS, setIncludeCCinSTS] = useState(false)
  const [basicMode, setBasicMode] = useState(false)

  useEffect(() => {
    if (profile) {
      setHintsEnabled(profile.hints_enabled !== false)
      setIncludeCCinSTS(profile.include_cc_in_sts === true)
      setBasicMode(profile.basic_mode === true)
    }
  }, [profile])

  async function toggleHints() {
    const newVal = !hintsEnabled
    setHintsEnabled(newVal)
    localStorage.setItem('tic_hints_enabled', newVal ? 'true' : 'false')
    window.dispatchEvent(new Event('storage'))
    await supabase.from('profiles').update({ hints_enabled: newVal }).eq('id', user.id)
    await fetchProfile(user.id)
  }

  async function toggleCCinSTS() {
    const newVal = !includeCCinSTS
    setIncludeCCinSTS(newVal)
    await supabase.from('profiles').update({ include_cc_in_sts: newVal }).eq('id', user.id)
    await fetchProfile(user.id)
  }

  async function toggleBasicMode() {
    const newVal = !basicMode
    setBasicMode(newVal)
    await supabase.from('profiles').update({ basic_mode: newVal }).eq('id', user.id)
    await fetchProfile(user.id)
  }

  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        income_type: profile.income_type || 'single',
        income_1: String(profile.income_1 || ''),
        income_2: String(profile.income_2 || ''),
        income_1_label: profile.income_1_label || 'My Income',
        income_2_label: profile.income_2_label || 'Partner Income',
        currency: profile.currency || 'GBP',
        savings_rate_target: profile.savings_rate_target || 20,
        emergency_fund_target_months: profile.emergency_fund_target_months || 6,
        pay_day: profile.pay_day || '',
        pay_day_shift_weekends: profile.pay_day_shift_weekends !== false,
      })
    }
  }, [profile])

  async function handleDeleteAccount() {
    setDeleting(true)
    setDeleteErr('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) { setDeleteErr('Your session has expired - please sign in again.'); setDeleting(false); return }

      const res = await fetch('/.netlify/functions/delete-account', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
      const result = await res.json().catch(() => ({}))

      if (!res.ok || !result.success) {
        setDeleteErr(result.error || 'Something went wrong deleting your account. Please try again.')
        setDeleting(false)
        return
      }

      // Fully deleted - show the confirmation screen. The auth user is gone so the
      // session is already dead server-side; we sign out when they leave the screen.
      setDeleting(false)
      setDeleted(true)
    } catch (e) {
      setDeleteErr('Could not reach the server. Please try again.')
      setDeleting(false)
    }
  }


  async function saveProfile() {
    const textFields = [form.full_name, form.income_1_label, form.income_2_label]
    if (textFields.some(containsSensitiveData)) {
      setSaveError(SENSITIVE_DATA_ERROR); return
    }
    setSaveError(''); setSaving(true)

    const oldPayDay = profile?.pay_day
    const newPayDay = form.pay_day ? parseInt(form.pay_day) : null
    const shiftWeekends = form.pay_day_shift_weekends !== false
    if (newPayDay && newPayDay !== oldPayDay) {
      const oldCycle = oldPayDay ? getPayCycle(oldPayDay, new Date(), shiftWeekends) : null
      const newCycle = getPayCycle(newPayDay, new Date(), shiftWeekends)
      if (oldCycle && newCycle && oldCycle.startIso !== newCycle.startIso) {
        const { data: oldBudgets } = await supabase
          .from('budgets').select('*')
          .eq('user_id', user.id).eq('month', oldCycle.startIso)
        if (oldBudgets && oldBudgets.length > 0) {
          const migrated = oldBudgets.map(b => ({ ...b, month: newCycle.startIso }))
          await supabase.from('budgets').upsert(migrated, { onConflict: 'user_id,month,category' })
        }
      }
    }

    await supabase.from('profiles').update({
      full_name: form.full_name,
      currency: form.currency,
      savings_rate_target: parseFloat(form.savings_rate_target) || 20,
      emergency_fund_target_months: parseFloat(form.emergency_fund_target_months) || 6,
      pay_day: newPayDay,
      pay_day_shift_weekends: shiftWeekends,
      income_type: form.income_type === 'dual' ? 'dual' : 'single',
      income_1_label: form.income_1_label || 'Me',
      income_2_label: form.income_2_label || 'Partner',
    }).eq('id', user.id)

    await fetchProfile(user.id)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }
  async function changePassword() {
    if (pw.next.length < 8) { setPwMsg('Password must be at least 8 characters'); return }
    if (!/[A-Z]/.test(pw.next)) { setPwMsg('Password must contain an uppercase letter'); return }
    if (!/[a-z]/.test(pw.next)) { setPwMsg('Password must contain a lowercase letter'); return }
    if (!/[0-9]/.test(pw.next)) { setPwMsg('Password must contain a number'); return }
    if (!/[^A-Za-z0-9]/.test(pw.next)) { setPwMsg('Password must contain a special character'); return }
    if (pw.next !== pw.confirm) { setPwMsg('Passwords do not match'); return }
    setPwSaving(true); setPwMsg('')
    const { error } = await supabase.auth.updateUser({ password: pw.next })
    setPwSaving(false)
    if (error) setPwMsg(error.message)
    else { setPwMsg('Password updated successfully'); setPw({ current: '', next: '', confirm: '' }) }
  }

  if (deleted) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '440px' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '26px', color: colors.text, marginBottom: '12px' }}>Your account has been deleted</div>
          <div style={{ fontSize: '15px', color: colors.mutedDeep, fontFamily: fonts.body, lineHeight: 1.65, marginBottom: '24px' }}>
            Everything has been permanently erased and you have been signed out. Sorry to see you go.
          </div>
          <button
            onClick={async () => { try { await signOut() } catch (e) {} window.location.href = 'https://theinvestingcouple.co.uk/' }}
            style={{ background: colors.gold, border: 'none', color: colors.onAccent, fontSize: '15px', fontFamily: fonts.body, fontWeight: 700, padding: '13px 22px', borderRadius: '10px', cursor: 'pointer', letterSpacing: '-0.01em', minHeight: '46px' }}
          >
            Return to the homepage
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Settings</div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Profile and preferences</div>
        </div>
      </div>

      <div style={{ padding: '14px 16px 0', maxWidth: '680px' }}>

        {/* Profile */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", color: colors.text, marginBottom: "14px" }}>PROFILE</div>
          <Input label="Full name" value={form.full_name} onChange={v => setF('full_name', v)} placeholder="Your name" />
          <Select label="Currency" value={form.currency} onChange={v => setF('currency', v)} options={[
            { value: 'GBP', label: '£ British Pound' },
            { value: 'EUR', label: '€ Euro' },
            { value: 'USD', label: '$ US Dollar' },
          ]} />
        </div>

        {/* Household - single vs dual income, and the person labels that drive
            owner tags on income, accounts, and bills. Writes structure only;
            income amounts live on the Income tab. */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", color: colors.text, marginBottom: "14px" }}>HOUSEHOLD</div>
          <label style={{ display: 'block', fontSize: '14px', color: colors.muted, letterSpacing: '-0.01em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '8px' }}>
            Who's this for?
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: form.income_type === 'dual' ? '16px' : '4px' }}>
            {[{ v: 'single', label: '👤 Just me' }, { v: 'dual', label: '👥 Two of us' }].map(opt => (
              <button
                key={opt.v}
                onClick={() => setF('income_type', opt.v)}
                style={{
                  padding: '14px',
                  background: form.income_type === opt.v ? 'rgba(201,151,43,0.1)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${form.income_type === opt.v ? colors.gold : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '10px', cursor: 'pointer',
                  color: form.income_type === opt.v ? colors.gold : colors.muted,
                  fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, minHeight: '48px',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {form.income_type === 'dual' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Input label="First person" value={form.income_1_label === 'My Income' ? '' : form.income_1_label} onChange={v => setF('income_1_label', v)} placeholder="e.g. Oliver" />
              <Input label="Second person" value={form.income_2_label === 'Partner Income' ? '' : form.income_2_label} onChange={v => setF('income_2_label', v)} placeholder="e.g. Vikki" />
            </div>
          )}
          <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '10px', lineHeight: 1.5 }}>
            These names label who owns each income source, account, and bill. Switching to “Just me” keeps any existing tags but hides the per-person breakdown.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {saveError && <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '10px', flex: 1 }}>{saveError}</div>}
            <GoldButton onClick={saveProfile}>{saving ? 'Saving...' : saved ? '✓ Saved' : 'Save changes'}</GoldButton>
          </div>
        </div>

        {/* Savings target (income now lives in the Income tab) */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", color: colors.text, marginBottom: "14px" }}>SAVINGS TARGET</div>

          <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.5, marginBottom: '16px' }}>
            Your income is now managed on the{' '}
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'income' }))} style={{ background: 'none', border: 'none', padding: 0, color: colors.gold, fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Income page →</button>
          </div>

          {/* Savings rate target */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', color: colors.muted, letterSpacing: '-0.01em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>
              Savings rate target
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input type="range" min="5" max="60" step="5" value={form.savings_rate_target || 20} onChange={e => setF('savings_rate_target', e.target.value)} style={{ flex: 1, accentColor: colors.gold }} />
              <span style={{ fontSize: '16px', color: colors.gold, fontFamily: fonts.heading, fontWeight: 700, width: '50px', textAlign: 'right' }}>{form.savings_rate_target || 20}%</span>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '6px' }}>
              The 50/30/20 rule recommends 20%. Adjust to match your circumstances.
            </div>
          </div>

          {/* Emergency fund target */}
          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: colors.muted, letterSpacing: '-0.01em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>
              Emergency fund target
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input type="range" min="1" max="12" step="1" value={form.emergency_fund_target_months || 6} onChange={e => setF('emergency_fund_target_months', e.target.value)} style={{ flex: 1, accentColor: colors.gold }} />
              <span style={{ fontSize: '16px', color: colors.gold, fontFamily: fonts.heading, fontWeight: 700, width: '70px', textAlign: 'right' }}>{form.emergency_fund_target_months || 6} mo</span>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '6px' }}>
              Many people aim for 3-6 months of expenses. Set the target that feels right for you.
            </div>
          </div>

          {/* Pay day */}
          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: colors.muted, letterSpacing: '-0.01em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>
              Pay day
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body }}>Paid on day</span>
              <input type="number" min="1" max="31" value={form.pay_day} onChange={e => setF('pay_day', e.target.value)} placeholder=" - " style={{ width: '70px', padding: '8px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: colors.text, fontFamily: fonts.heading, fontSize: '16px', textAlign: 'center' }} />
              <span style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body }}>of the month</span>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '6px' }}>
              Powers the “safe to spend until pay day” figure and your budget periods. Leave blank if your pay date varies.
            </div>

            {/* Weekend-shift toggle - only relevant once a pay day is set. */}
            {form.pay_day ? (
              <div
                onClick={() => setF('pay_day_shift_weekends', !form.pay_day_shift_weekends)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', cursor: 'pointer' }}
              >
                <div style={{
                  width: '44px', height: '26px', borderRadius: '13px', flexShrink: 0,
                  background: form.pay_day_shift_weekends ? colors.gold : 'rgba(255,255,255,0.12)',
                  position: 'relative', transition: 'background 0.15s',
                }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '3px', left: form.pay_day_shift_weekends ? '21px' : '3px',
                    transition: 'left 0.15s',
                  }} />
                </div>
                <div>
                  <div style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.body, fontWeight: 600 }}>
                    Pay day shifts earlier on weekends
                  </div>
                  <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>
                    If pay day lands on a Saturday or Sunday, it shifts to the nearest weekday. Most UK employers do this.
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {saveError && <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '10px', flex: 1 }}>{saveError}</div>}
            <GoldButton onClick={saveProfile}>{saving ? 'Saving...' : saved ? '✓ Saved' : 'Save changes'}</GoldButton>
          </div>
        </div>

        {/* Change password */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", color: colors.text, marginBottom: "14px" }}>CHANGE PASSWORD</div>
          <Input label="New password" type="password" value={pw.next} onChange={v => setPw(p => ({ ...p, next: v }))} placeholder="Enter new password" />
          {pw.next.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '-4px 0 8px' }}>
              {[
                { label: '8+ chars', ok: pw.next.length >= 8 },
                { label: 'Uppercase', ok: /[A-Z]/.test(pw.next) },
                { label: 'Lowercase', ok: /[a-z]/.test(pw.next) },
                { label: 'Number', ok: /[0-9]/.test(pw.next) },
                { label: 'Special (!@#...)', ok: /[^A-Za-z0-9]/.test(pw.next) },
              ].map(r => (
                <span key={r.label} style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: r.ok ? 'rgba(61,184,122,0.12)' : 'rgba(255,255,255,0.05)', color: r.ok ? '#3db87a' : `rgba(${colors.textRgb},0.35)`, border: `1px solid ${r.ok ? 'rgba(61,184,122,0.3)' : 'rgba(255,255,255,0.08)'}`, transition: 'all 0.2s' }}>
                  {r.ok ? '✓ ' : ''}{r.label}
                </span>
              ))}
            </div>
          )}
          <Input label="Confirm new password" type="password" value={pw.confirm} onChange={v => setPw(p => ({ ...p, confirm: v }))} placeholder="Repeat password" />
          {pwMsg && (
            <div style={{ fontSize: '14px', fontFamily: fonts.body, marginBottom: '12px', color: pwMsg.includes('success') ? colors.green : colors.red }}>
              {pwMsg}
            </div>
          )}
          <GoldButton onClick={changePassword}>{pwSaving ? 'Updating...' : 'Update password'}</GoldButton>
        </div>

        {/* Appearance */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '4px' }}>APPEARANCE</div>
          <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginBottom: '14px' }}>Pick a colour theme. Saved to your account, so it follows you across devices.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {THEMES.map(t => {
              const active = t.id === themeId
              return (
                <button key={t.id} onClick={() => setTheme(t.id, user)} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
                  background: active ? 'rgba(255,255,255,0.04)' : 'transparent',
                  border: `1px solid ${active ? colors.gold : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '12px', cursor: 'pointer', textAlign: 'left', minHeight: '54px',
                }}>
                  <span style={{ width: '34px', height: '34px', borderRadius: '9px', background: t.swatch[0], border: '1px solid rgba(255,255,255,0.12)', position: 'relative', flexShrink: 0, overflow: 'hidden' }}>
                    <span style={{ position: 'absolute', right: '4px', bottom: '4px', width: '12px', height: '12px', borderRadius: '50%', background: t.swatch[1] }} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 600 }}>{t.label}</span>
                    <span style={{ display: 'block', fontSize: '12px', color: active ? colors.gold : colors.muted, fontFamily: fonts.body, marginTop: '1px' }}>{active ? 'Selected' : (t.mode === 'dark' ? 'Dark' : 'Light')}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Preferences */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", color: colors.text, marginBottom: "14px" }}>PREFERENCES</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '4px' }}>
            <div>
              <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>Basic mode</div>
              <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>Simplify the app - bills, budget and STS only. Switch off to unlock all features</div>
            </div>
            <button onClick={toggleBasicMode} style={{
              width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
              background: basicMode ? colors.gold : 'rgba(255,255,255,0.15)',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}>
              <div style={{
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                position: 'absolute', top: '3px',
                left: basicMode ? '23px' : '3px',
                transition: 'left 0.2s',
              }} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
            <div>
              <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>Financial tips & glossary hints</div>
              <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>Show "?" tooltips and contextual tips throughout the app</div>
            </div>
            <button onClick={toggleHints} style={{
              width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
              background: hintsEnabled ? colors.gold : 'rgba(255,255,255,0.15)',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}>
              <div style={{
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                position: 'absolute', top: '3px',
                left: hintsEnabled ? '23px' : '3px',
                transition: 'left 0.2s',
              }} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '4px' }}>
            <div>
              <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>Include credit card bills in Safe to Spend</div>
              <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>When on, bills and spending paid via credit card reduce your Safe to Spend figure</div>
            </div>
            <button onClick={toggleCCinSTS} style={{
              width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
              background: includeCCinSTS ? colors.gold : 'rgba(255,255,255,0.15)',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}>
              <div style={{
                width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                position: 'absolute', top: '3px',
                left: includeCCinSTS ? '23px' : '3px',
                transition: 'left 0.2s',
              }} />
            </button>
          </div>
        </div>

        {/* Import */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", color: colors.text, marginBottom: "14px" }}>IMPORT DATA</div>
          <p style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '14px' }}>
            Bulk import accounts or bills from a CSV template. Download the template, fill it in, then upload it to preview and confirm before anything is saved.
          </p>
          <GoldButton onClick={() => onNavigate?.('import')}>Go to Import →</GoldButton>
        </div>

        {/* App preferences */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '14px' }}>APP PREFERENCES</div>
          <button onClick={() => {
            localStorage.removeItem('tic_walkthrough_complete_' + (user?.id || ''))
            window.location.reload()
          }} style={{ width: '100%', padding: '12px', background: 'rgba(201,151,43,0.08)', border: '1px solid rgba(201,151,43,0.25)', borderRadius: '10px', color: colors.gold, fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
            Replay app walkthrough
          </button>
        </div>

        {/* Danger zone - full account deletion */}
        <div style={{ background: colors.card, border: '1px solid rgba(224,82,82,0.25)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px', padding: '16px' }}>
          <CardTitle><span style={{ color: colors.red }}>Delete account</span></CardTitle>
          {!showDelete ? (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '14px' }}>
                Permanently delete your entire Investing Couple account - the one login for both the website courses and this tracker. This erases all your tracker data (accounts, budgets, income, goals, debts, bills and history) and removes your course access. You will be signed out of both. This cannot be undone.
              </div>
              <button onClick={() => { setShowDelete(true); setDeleteErr('') }} style={{ background: 'transparent', border: `1px solid ${colors.red}`, color: colors.red, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, padding: '11px 18px', borderRadius: '10px', cursor: 'pointer', letterSpacing: '-0.01em', minHeight: '44px' }}>
                Delete my account
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '12px' }}>
                This will <strong>permanently erase everything</strong> and sign you out. There's no way to recover it. To confirm, type <strong>DELETE</strong> below.
              </div>
              <input
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="Type DELETE to confirm"
                style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(224,82,82,0.4)`, borderRadius: '10px', color: colors.text, fontFamily: fonts.body, fontSize: '16px', marginBottom: '12px', boxSizing: 'border-box' }}
              />
              {deleteErr && (
                <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, marginBottom: '12px' }}>{deleteErr}</div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirm !== 'DELETE' || deleting}
                  style={{ background: deleteConfirm === 'DELETE' && !deleting ? colors.red : 'rgba(224,82,82,0.3)', border: 'none', color: '#fff', fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, padding: '11px 18px', borderRadius: '10px', cursor: deleteConfirm === 'DELETE' && !deleting ? 'pointer' : 'not-allowed', letterSpacing: '-0.01em', minHeight: '44px' }}
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
                <GhostButton onClick={() => { setShowDelete(false); setDeleteConfirm(''); setDeleteErr('') }}>Cancel</GhostButton>
              </div>
            </div>
          )}
        </div>

        {/* Privacy */}
        <div style={{ padding: '12px 0', textAlign: 'center' }}>
          <a href="https://theinvestingcouple.co.uk/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, textDecoration: 'underline' }}>
            Privacy Policy
          </a>
          <span style={{ color: colors.mutedDeep, margin: '0 8px', fontFamily: fonts.body }}>·</span>
          <span style={{ fontSize: '14px', color: colors.mutedDeep, fontFamily: fonts.body }}>
            The Investing Couple Budget Tracker
          </span>
        </div>

      </div>
    </div>
  )
}
